import React, { useEffect, useState } from 'react';
import { FiltersMap, ModalState } from '@wix/bex-core';
import { Box, Button, IconButton, Modal, Tooltip } from '@wix/design-system';
import { UploadExport } from '@wix/wix-ui-icons-common';
import { observer, Observer } from 'mobx-react-lite';
import { ExportButtonModalLayout } from './ExportButtonModalLayout';
import { useWixPatternsContainer } from '@wix/bex-core/react';
import { useToolbarCollectionContext } from '../ToolbarCollectionContext';
import { ExportButtonProps } from './ExportProps';

function _ExportButton<T, F extends FiltersMap>(
  props: ExportButtonProps<T, F>,
) {
  const {
    toolbarType = 'default',
    renderError,
    transformPlatformizedQuery,
    differentQueryEndpointThanCollection,
    customTotal,
    methodMetadata,
    arrayFieldDelimiter,
    methodSpec,
    fields,
    saveAs,
    exportModalFooter,
    exportModalTitle,
    onProgressModalTitle,
    exportToCSVMessage,
  } = props;

  const table = useToolbarCollectionContext<T, F>();

  const { translate: t } = useWixPatternsContainer();

  const [state] = useState(() => (table.exportState ??= new ModalState()));

  useEffect(() => {
    table.featuredComponents.exportModal.set(
      <Observer>
        {() => (
          <Modal
            onRequestClose={state.requestClose}
            isOpen={state.isOpen}
            dataHook="export-modal"
          >
            <ExportButtonModalLayout
              exportModalState={state}
              renderError={renderError}
              transformPlatformizedQuery={transformPlatformizedQuery}
              customTotal={customTotal}
              arrayFieldDelimiter={arrayFieldDelimiter}
              differentQueryEndpointThanCollection={
                differentQueryEndpointThanCollection
              }
              methodMetadata={methodMetadata}
              methodSpec={methodSpec}
              fields={fields}
              saveAs={saveAs}
              exportModalFooter={exportModalFooter}
              exportModalTitle={exportModalTitle}
              onProgressModalTitle={onProgressModalTitle}
              exportToCSVMessage={exportToCSVMessage}
            />
          </Modal>
        )}
      </Observer>,
    );

    return () => {
      table.featuredComponents.exportModal.set(null);
    };
  }, []);

  return (
    <Box dataHook="export-container">
      {toolbarType === 'default' ? (
        <Tooltip
          dataHook="export-button-tooltip"
          content={t('cairo.export.export')}
        >
          <IconButton
            priority="secondary"
            dataHook="export-button"
            onClick={state.open}
            size="small"
          >
            <UploadExport />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          skin="standard"
          priority="secondary"
          size="small"
          prefixIcon={<UploadExport />}
          dataHook="export-button"
          onClick={state.open}
        >
          {t('cairo.export.export')}
        </Button>
      )}
    </Box>
  );
}

export const ExportButton = observer(_ExportButton);
